import {
  MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import PercentBar from '@atom/components/common/percentBar/PercentBar';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Checkbox, Icon, ListTable } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { BudgetCategory } from '@atom/types/budget';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import BudgetDetailComparison from '../BudgetDetailComparison';
import BudgetDetailContext from '../BudgetDetailContext';
import { getComparisonIcon, ICON_COL_WIDTH } from '../budgetDetailUtils';

import { BUDGET_CHECK_SX } from './BudgetDetailCategories';

import classes from '../budgetDetail.module.css';

const { TableRow, TableCell } = ListTable;

interface Props {
  budgetCategory: BudgetCategory;
  toggleRowOpen: (categoryId: string) => void;
}

const BudgetDetailCategoryRow = ({ budgetCategory, toggleRowOpen }: Props) => {
  const {
    budgetItemTemplateNames,
    budgetTypes,
    budgetSummaries,
    comparisonBudgets,
    openComparisonRowId,
    setOpenComparisonRowId,
    expandedCategories,
    parentBudgetUnit,
    fetchSummaries,
    loadingSummaries,
    showTracking,
    selectedItems,
    setSelectedItems,
    showAuthorizedBudget,
    selectItemCheckboxDisabled,
  } = useContext(BudgetDetailContext);

  const preferences = usePreferences();
  const hideCostType = R.pathOr(
    false,
    ['budgeting', 'hideCostType'],
    preferences,
  );

  // Next four consts track state of category checkbox derived from selected children
  const childItemIds = useMemo(
    () => R.pluck('id', budgetCategory?.budgetItems),
    [budgetCategory?.budgetItems],
  );
  const selectedItemCount = useMemo(
    () => R.length(R.intersection(childItemIds, [...selectedItems])),
    [childItemIds, selectedItems],
  );
  const allChildrenChecked = useMemo(
    () => selectedItemCount === R.length(childItemIds),
    [childItemIds, selectedItemCount],
  );
  const indeterminate = useMemo(
    () => selectedItemCount && selectedItemCount < R.length(childItemIds),
    [childItemIds, selectedItemCount],
  );

  const [hovering, setHovering] = useState<boolean>(false);

  const open: boolean = useMemo(
    () => openComparisonRowId === budgetCategory.id,
    [openComparisonRowId],
  );

  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && !isNilOrEmpty(comparisonBudgets)) {
      fetchSummaries({
        variables: {
          input: {
            budgetUnitId: parentBudgetUnit.id,
            budgetIds: R.pluck('id', comparisonBudgets),
            categoryIds: [budgetCategory.id],
            itemNames: budgetItemTemplateNames,
            budgetTypes,
          },
        },
      });
    }
  }, [open, comparisonBudgets]);

  const handleCompareClick = (event: MouseEvent) => {
    event.stopPropagation();
    setOpenComparisonRowId(open ? null : budgetCategory.id);
  };

  const handleToggleCategory = () => {
    const newSelectedItems = allChildrenChecked
      ? new Set(R.without(childItemIds, [...selectedItems]))
      : new Set([...childItemIds, ...selectedItems]);

    setSelectedItems(newSelectedItems);
  };

  return (
    <>
      <TableRow
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        hovercolor={colors.neutral.fog}
        style={{
          backgroundColor: open ? colors.neutral.fog : colors.neutral.white,
        }}
      >
        <TableCell>
          {showAuthorizedBudget && (
            <Checkbox
              checked={allChildrenChecked}
              indeterminate={indeterminate}
              onClick={() => handleToggleCategory()}
              sx={BUDGET_CHECK_SX}
              disabled={selectItemCheckboxDisabled}
            />
          )}
          <span
            onClick={() => toggleRowOpen(budgetCategory.id)}
            className={clsx(
              classes.budgetTableCell,
              classes.categoryNameCell,
              classes.clickable,
            )}
          >
            <Icon
              icon={
                expandedCategories.has(budgetCategory.id)
                  ? ArrowDropDownIcon
                  : ArrowRightIcon
              }
            />
            {budgetCategory.name}
          </span>
        </TableCell>
        {!hideCostType && (
          <TableCell>
            <span className={classes.budgetTableCell} />
          </TableCell>
        )}
        <TableCell align="right">
          <span
            className={clsx(classes.budgetTableCell, classes.budgetTotalCell)}
          >
            {numberToLocaleString(budgetCategory?.totalBudget)}
          </span>
        </TableCell>
        {showTracking && (
          <>
            <TableCell align="right">
              <span
                className={clsx(
                  classes.budgetTableCell,
                  classes.budgetTotalCell,
                )}
              >
                {numberToLocaleString(budgetCategory?.actualBudget)}
              </span>
            </TableCell>
            <TableCell align="right">
              <span
                className={clsx(
                  classes.budgetTableCell,
                  classes.budgetTotalCell,
                )}
              >
                {numberToLocaleString(budgetCategory?.fixedCosts)}
              </span>
            </TableCell>
            <TableCell align="right">
              <PercentBar
                value={budgetCategory?.remaining}
                limit={budgetCategory?.totalBudget}
              />
            </TableCell>
          </>
        )}
        <TableCell>
          <span className={classes.budgetTableCell} />
        </TableCell>
        <TableCell width={ICON_COL_WIDTH}>
          <span
            ref={anchorRef}
            onClick={handleCompareClick}
            className={clsx(classes.budgetTableCell, classes.clickable)}
          >
            {getComparisonIcon(hovering, open)}
          </span>
        </TableCell>
      </TableRow>
      {open && (
        <BudgetDetailComparison
          handleCloseModal={() => setOpenComparisonRowId(null)}
          loadingSummaries={loadingSummaries}
          budgetSummaries={budgetSummaries}
          includeComments
          anchorEl={anchorRef?.current}
          onClickAway={() => setOpenComparisonRowId(null)}
          hideComments
        />
      )}
    </>
  );
};

export default BudgetDetailCategoryRow;
